import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import Occlusion from 'src/components/Occlusion/Occlusion'
import { AuthTypes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  deleteCognitoSignout,
  deleteSignout,
  postAuth0RenewAuth,
  postCognitoRenewAuth,
} from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectDecodedCspJwt,
  selectDecodedJwt,
  selectEmailFromState,
  selectIsAuthenticatedFromState,
  selectIsCspAuthenticatedFromState,
  selectIsRenewingAuthFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css, keyframes } from 'styled-components'

// TODO: define animations in extend-web-shared
const occlusionFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 0.75;
  }
`

const SessionExpiringPopup: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const decodedJwt = useSelector(selectDecodedJwt)
  const decodedCspJwt = useSelector(selectDecodedCspJwt)
  const isRenewingAuth = useSelector(selectIsRenewingAuthFromState)
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const authEmail = useSelector(selectEmailFromState)
  const user = useSelector(selectUserListModelFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const authType = useSelector(selectAuthTypeFromState)

  const email = user?.email || authEmail
  const [secondsRemaining, setSecondsRemaining] = useState(0)
  const countdownInterval = useRef<NodeJS.Timeout | undefined>()

  const { analytics } = useAnalyticsContext()

  const dispatchDeleteSignout = (
    didSessionExpireAutomatically: boolean,
  ): void => {
    if (didSessionExpireAutomatically) {
      analytics?.track('Still Working - Refresh Token - Button Press', {
        intent: 'no intent - automatic logout due to timeout',
        interaction: 'button press',
      })
    } else {
      analytics?.track('Still Working - Refresh Token - Button Press', {
        intent: 'logout',
        interaction: 'button press',
      })
    }
    if (authType === AuthTypes.auth0) {
      dispatch(
        deleteSignout(
          didSessionExpireAutomatically,
          analytics,
          isCspAuthenticated,
          calculatedBackgroundLocation.pathname,
        ),
      )
    } else {
      dispatch(
        deleteCognitoSignout(
          didSessionExpireAutomatically,
          analytics,
          isCspAuthenticated,
        ),
      )
    }
  }

  useEffect(() => {
    if (isCspAuthenticated && decodedCspJwt !== undefined) {
      setSecondsRemaining(
        Math.round(
          moment(new Date(decodedCspJwt.exp * 1000)).diff(moment()) / 1000,
        ),
      )
      countdownInterval.current = setInterval((): void => {
        const msUntilJwtExpiration = moment(
          new Date(decodedCspJwt.exp * 1000),
        ).diff(moment())
        setSecondsRemaining(
          Math.round(
            moment(new Date(decodedCspJwt.exp * 1000)).diff(moment()) / 1000,
          ),
        )
        if (msUntilJwtExpiration < 0) {
          dispatchDeleteSignout(true)
        }
      }, 1000)
    } else if (isAuthenticated && decodedJwt !== undefined) {
      setSecondsRemaining(
        Math.round(
          moment(new Date(decodedJwt.exp * 1000)).diff(moment()) / 1000,
        ),
      )
      countdownInterval.current = setInterval((): void => {
        const msUntilJwtExpiration = moment(
          new Date(decodedJwt.exp * 1000),
        ).diff(moment())
        setSecondsRemaining(
          Math.round(
            moment(new Date(decodedJwt.exp * 1000)).diff(moment()) / 1000,
          ),
        )
        if (msUntilJwtExpiration < 0) {
          dispatchDeleteSignout(true)
        }
      }, 1000)
    }
    return (): void => {
      if (countdownInterval.current !== undefined) {
        clearInterval(countdownInterval.current)
      }
    }
  }, [])

  // redundant logic to trigger the signout if the countdown somehow goes below 0
  useEffect(() => {
    if (secondsRemaining < 0) {
      dispatchDeleteSignout(true)
    }
  }, [secondsRemaining])

  return (
    <div
      css={css`
        z-index: 13;
        position: fixed;
        display: flex;
        flex-direction: column;
        align-items: center;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        overflow: scroll;
      `}
    >
      <Occlusion isOpen fadeIn />
      <div
        css={css`
          margin: 120px 0px;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          animation: ${occlusionFadeIn} $transition-timing-slowest 1;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            margin: 60px 10px;
            align-self: stretch;
          `)}

          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            margin: 40px 5px;
          `)}
        `}
      >
        <div
          css={css`
            background-color: ${styleConstants.colors.white};
            border-radius: 10px;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
            width: 700px;
            padding: 60px;
            flex: 1 0 auto;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              flex-grow: 2;
              width: 100%;
              padding: 40px;
            `)}

            ${getBreakpointOnly(breakpointSizes.XXSM)(css`
              padding: 40px 20px;
            `)}
          `}
          data-testid="session-expiring-popup"
        >
          <ConfirmationPopupContent
            title={translate('Are you still working?')}
            subtitle={translate(
              "We've noticed you've been inactive for a while, and will automatically end your session in ${countSeconds}. Would you like to continue working?",
              {
                countSeconds: (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate('${count} seconds', {
                      count: `${secondsRemaining}`,
                    })}
                  </span>
                ),
              },
            )}
            actionButtonLabel={translate('Yes, continue working')}
            actionButtonOnClick={(): void => {
              authType === AuthTypes.cognito
                ? dispatch(postCognitoRenewAuth(true, isCspAuthenticated))
                : dispatch(postAuth0RenewAuth(email, isCspAuthenticated, true))
              analytics?.track('Still Working - Refresh Token - Button Press', {
                intent: 'contiue working',
                interaction: 'button press',
              })
            }}
            actionButtonLoading={isRenewingAuth}
            actionButtonVariant="primary"
            cancelButtonLabel={translate('No, log out')}
            cancelButtonVariant="severe"
            cancelButtonOnClick={(): void => {
              dispatchDeleteSignout(false)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default SessionExpiringPopup
